/**
 * Reads how long the Keycloak sign-in really lasts from the tokens oidc-client-ts
 * keeps for the signed-in user.
 *
 * - The ACCESS token is short-lived (the realm's Access Token Lifespan). It is
 *   renewed with the refresh token, so its expiry is not the end of the sign-in.
 * - The REFRESH token is a JWT in Keycloak. Its `exp` is the real end: Keycloak
 *   sets it to the earlier of "last renewal + SSO Session Idle" and "sign-in time
 *   + SSO Session Max" (client overrides included), so the session maximum is
 *   already in it. There is no separate session-max claim in the id or refresh
 *   token to read.
 * - `refresh_expires_in` from the token response is not kept: oidc-client-ts's
 *   User drops it. So when the refresh token cannot be decoded, the end is
 *   unknown and no warning is shown (never a warning based on the access token).
 */

export interface TokenUser {
  access_token?: string;
  refresh_token?: string;
  /** Access token expiry, epoch seconds, computed on THIS computer's clock by oidc-client-ts */
  expires_at?: number;
}

export interface SessionTimes {
  /** Access token expiry, epoch seconds, local clock */
  accessExpiresAt: number;
  /** Access token lifetime in seconds, when the token says (exp - iat) */
  accessLifetime: number | null;
  /** End of the sign-in (refresh token exp), epoch seconds, local clock; null = unknown or no end */
  sessionEndsAt: number | null;
  /** True when the user has a refresh token at all */
  hasRefreshToken: boolean;
}

/** The payload of a JWT, or null when the token is not a readable JWT. Never verifies anything. */
export function decodeJwtPayload(token: string | undefined | null): Record<string, unknown> | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const json = decodeURIComponent(
      Array.from(atob(b64), (c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(''),
    );
    const payload: unknown = JSON.parse(json);
    return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Session times on this computer's clock.
 *
 * Token `exp`/`iat` claims are on Keycloak's clock; `expires_at` is on ours.
 * The difference between the access token's `exp` claim and `expires_at` is the
 * clock offset, which is applied to the refresh token's `exp` so a computer with
 * a wrong clock does not warn early or late.
 */
export function readSessionTimes(user: TokenUser | null | undefined): SessionTimes | null {
  if (!user?.access_token || !num(user.expires_at)) return null;
  const accessExpiresAt = user.expires_at as number;

  const access = decodeJwtPayload(user.access_token);
  const accessExp = num(access?.exp);
  const accessIat = num(access?.iat);
  const accessLifetime = accessExp !== null && accessIat !== null && accessExp > accessIat ? accessExp - accessIat : null;
  // Keycloak clock minus ours. Only trusted when it is a real difference, not latency.
  const rawOffset = accessExp !== null ? accessExp - accessExpiresAt : 0;
  const offset = Math.abs(rawOffset) > 5 ? rawOffset : 0;

  let sessionEndsAt: number | null = null;
  const refresh = decodeJwtPayload(user.refresh_token);
  const refreshExp = num(refresh?.exp);
  // Offline tokens carry exp 0 (or none) when they have no maximum: no end to warn about.
  if (refreshExp !== null && refreshExp > 0) sessionEndsAt = refreshExp - offset;

  return {
    accessExpiresAt,
    accessLifetime,
    sessionEndsAt,
    hasRefreshToken: !!user.refresh_token,
  };
}
